/**
 * TicketManagementScreen — Support Team / Admin view of all ERP tickets.
 * Two-way integration: clients raise tickets via SmartAssistantPanel → 
 * support team responds here → client sees updates in their panel.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { getToken } from '../../lib/api';
import { API_BASE } from '../../lib/api';
import {
  Ticket, Search, RefreshCw, ChevronLeft, Send, Paperclip,
  AlertTriangle, CheckCircle, Clock, User, Filter, Download,
  MessageSquare, Tag, ArrowUpRight, ArrowDownRight, X, Plus, Eye
} from 'lucide-react';

const API = `${API_BASE}/api/smart-assistant`;

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${getToken()}`
});

const STATUSES = ['Open', 'In Progress', 'Resolved', 'Closed'];
const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];
const CATEGORIES = ['General', 'Bug', 'Feature Request', 'Question', 'Finance', 'Inventory', 'Reports', 'User Management', 'Other'];

const priorityColor = (p) => ({
  Critical: 'bg-red-600 text-white',
  High: 'bg-red-100 text-red-700',
  Medium: 'bg-amber-100 text-amber-700',
  Low: 'bg-green-100 text-green-700',
})[p] || 'bg-gray-100 text-gray-600';

const statusColor = (s) => ({
  Open: 'bg-blue-100 text-blue-700',
  'In Progress': 'bg-amber-100 text-amber-700',
  Resolved: 'bg-teal-100 text-teal-700',
  Closed: 'bg-green-100 text-green-700',
})[s] || 'bg-gray-100 text-gray-600';

const statusIcon = (s) => {
  if (s === 'Open') return <AlertTriangle size={11} />;
  if (s === 'In Progress') return <Clock size={11} />;
  if (s === 'Resolved' || s === 'Closed') return <CheckCircle size={11} />;
  return null;
};

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) +
    ' ' + dt.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
};

const timeAgo = (d) => {
  if (!d) return '';
  const diff = Math.floor((Date.now() - new Date(d)) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

// ─────────────────────────────────────────
// Dashboard Stats Bar
// ─────────────────────────────────────────
const StatsBar = ({ stats, theme }) => {
  const isDark = theme === 'dark';
  if (!stats) return null;
  const cards = [
    { label: 'Open', value: stats.open, color: 'text-blue-600', bg: isDark ? 'bg-blue-500/10' : 'bg-blue-50' },
    { label: 'In Progress', value: stats.inProgress, color: 'text-amber-600', bg: isDark ? 'bg-amber-500/10' : 'bg-amber-50' },
    { label: 'Resolved', value: stats.resolved, color: 'text-teal-600', bg: isDark ? 'bg-teal-500/10' : 'bg-teal-50' },
    { label: 'Closed', value: stats.closed, color: 'text-green-600', bg: isDark ? 'bg-green-500/10' : 'bg-green-50' },
    { label: 'Critical', value: stats.critical, color: 'text-red-600', bg: isDark ? 'bg-red-500/10' : 'bg-red-50' },
    { label: 'Unassigned', value: stats.unassigned, color: 'text-purple-600', bg: isDark ? 'bg-purple-500/10' : 'bg-purple-50' },
    { label: 'Today', value: stats.last24h, color: 'text-indigo-600', bg: isDark ? 'bg-indigo-500/10' : 'bg-indigo-50' },
    { label: 'Total', value: stats.total, color: isDark ? 'text-slate-300' : 'text-slate-700', bg: isDark ? 'bg-white/5' : 'bg-slate-100' },
  ];
  return (
    <div className="grid grid-cols-4 lg:grid-cols-8 gap-2 px-4 py-3 border-b" style={{ borderColor: isDark ? '#1f1f1f' : '#f0f0f0' }}>
      {cards.map(c => (
        <div key={c.label} className={`rounded-xl px-3 py-2 ${c.bg}`}>
          <div className={`text-xl font-black ${c.color}`}>{c.value ?? 0}</div>
          <div className={`text-[10px] font-semibold mt-0.5 ${isDark ? 'text-slate-500' : 'text-slate-500'}`}>{c.label}</div>
        </div>
      ))}
    </div>
  );
};

// ─────────────────────────────────────────
// Ticket Detail Panel
// ─────────────────────────────────────────
const TicketDetail = ({ ticket, onClose, onUpdated, theme }) => {
  const isDark = theme === 'dark';
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comment, setComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editStatus, setEditStatus] = useState(ticket.status);
  const [editPriority, setEditPriority] = useState(ticket.priority);
  const [editAssignedTo, setEditAssignedTo] = useState(ticket.assignedTo || '');

  // ── Helpers (defined before useEffect) ───────────────────────────────────────
  // Re-used by saveChanges & addComment after mutations
  const reload = async (ticketId) => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/tickets/detail/${ticketId}`, { headers: authHeaders() });
      const d = await r.json();
      if (d.success) { setDetail(d.data); setEditStatus(d.data.status); setEditPriority(d.data.priority); setEditAssignedTo(d.data.assignedTo || ''); }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const saveChanges = async () => {
    setSaving(true);
    try {
      await fetch(`${API}/tickets/${ticket.id}`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ status: editStatus, priority: editPriority, assignedTo: editAssignedTo || null })
      });
      onUpdated();
      reload(ticket.id);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const addComment = async () => {
    if (!comment.trim()) return;
    setSaving(true);
    try {
      await fetch(`${API}/tickets/${ticket.id}/comments`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ userId: 'support', content: comment, isInternal })
      });
      setComment('');
      reload(ticket.id);
      onUpdated();
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const downloadAttachment = async (att) => {
    const r = await fetch(`${API}/attachments/${att.id}/download`, { headers: authHeaders() });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = att.fileName;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };

  const inputCls = `w-full px-3 py-1.5 rounded-lg border text-xs outline-none ${isDark ? 'bg-[#1a1a1a] border-[#333] text-white' : 'bg-white border-gray-200 text-gray-900'}`;
  const labelCls = `block text-[10px] font-bold uppercase tracking-wider mb-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`;

  // ── useEffect — inline async IIFE, no external setState caller ────────────────
  useEffect(() => {
    const tid = ticket.id;
    const init = async () => {
      setLoading(true);
      try {
        const r = await fetch(`${API}/tickets/detail/${tid}`, { headers: authHeaders() });
        const d = await r.json();
        if (d.success) { setDetail(d.data); setEditStatus(d.data.status); setEditPriority(d.data.priority); setEditAssignedTo(d.data.assignedTo || ''); }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    init();
  }, [ticket.id]);

  return (
    <div className={`flex flex-col h-full border-l ${isDark ? 'bg-[#111] border-[#222]' : 'bg-white border-gray-200'}`}>
      {/* Header */}
      <div className={`shrink-0 flex items-center gap-3 px-4 py-3 border-b ${isDark ? 'border-[#222]' : 'border-gray-200'}`}>
        <button onClick={onClose} className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-gray-100 text-gray-500'}`}>
          <ChevronLeft size={16} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={`text-xs font-mono ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{ticket.ticketNumber}</span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${statusColor(editStatus)}`}>
              {statusIcon(editStatus)}{editStatus}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${priorityColor(editPriority)}`}>{editPriority}</span>
          </div>
          <p className={`text-sm font-semibold mt-0.5 truncate ${isDark ? 'text-white' : 'text-gray-900'}`}>{ticket.subject}</p>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left: Thread */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {loading ? (
              <div className="flex justify-center py-8"><RefreshCw size={18} className="animate-spin text-blue-500" /></div>
            ) : (
              <>
                {/* Description */}
                <div className={`p-3 rounded-xl border ${isDark ? 'bg-[#181818] border-[#2a2a2a]' : 'bg-gray-50 border-gray-100'}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-6 h-6 rounded-full bg-blue-600 flex items-center justify-center text-white text-xs font-bold">
                      {(detail?.userId || ticket.userId || 'U').charAt(0).toUpperCase()}
                    </div>
                    <span className={`text-xs font-semibold ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{detail?.userId || ticket.userId}</span>
                    <span className={`text-[10px] ml-auto ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>{fmtDate(detail?.createdAt)}</span>
                  </div>
                  <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'} whitespace-pre-wrap`}>{detail?.description || 'No description provided.'}</p>
                  {detail?.screenContext && (
                    <div className={`mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] ${isDark ? 'bg-slate-800 text-slate-400' : 'bg-slate-100 text-slate-500'}`}>
                      <Tag size={9} /> Screen: {detail.screenContext}
                    </div>
                  )}
                </div>

                {/* Comments */}
                {detail?.comments?.map((c, i) => (
                  <div key={i} className={`p-3 rounded-xl border ${c.isInternal
                    ? isDark ? 'bg-amber-500/5 border-amber-500/20' : 'bg-amber-50 border-amber-100'
                    : isDark ? 'bg-[#1a1a1a] border-[#2a2a2a]' : 'bg-white border-gray-100'}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${c.userId === 'support' ? 'bg-purple-600 text-white' : 'bg-blue-600 text-white'}`}>
                        {c.userId.charAt(0).toUpperCase()}
                      </div>
                      <span className={`text-xs font-semibold ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{c.userId}</span>
                      {c.isInternal && <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[9px] font-bold rounded">Internal</span>}
                      <span className={`text-[10px] ml-auto ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>{timeAgo(c.createdAt)}</span>
                    </div>
                    <p className={`text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'} whitespace-pre-wrap`}>{c.content}</p>
                  </div>
                ))}

                {/* Attachments */}
                {detail?.attachments?.length > 0 && (
                  <div className={`p-3 rounded-xl border ${isDark ? 'bg-[#1a1a1a] border-[#2a2a2a]' : 'bg-gray-50 border-gray-100'}`}>
                    <p className={`text-[10px] font-bold uppercase mb-2 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Attachments ({detail.attachments.length})</p>
                    <div className="space-y-1">
                      {detail.attachments.map(a => (
                        <div key={a.id} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs ${isDark ? 'bg-[#222]' : 'bg-white border border-gray-100'}`}>
                          <Paperclip size={11} className={isDark ? 'text-slate-500' : 'text-gray-400'} />
                          <span className={`flex-1 truncate ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{a.fileName}</span>
                          <span className={`text-[10px] ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>{(a.fileSize / 1024).toFixed(0)}KB</span>
                          <button onClick={() => downloadAttachment(a)} className="p-1 rounded hover:bg-blue-500/20 text-blue-500" title="Download">
                            <Download size={11} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Reply Box */}
          <div className={`shrink-0 p-3 border-t ${isDark ? 'border-[#222]' : 'border-gray-200'}`}>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Add a reply or note..."
              rows={3}
              className={`w-full px-3 py-2 rounded-xl border text-sm resize-none outline-none ${isDark ? 'bg-[#1a1a1a] border-[#333] text-white placeholder:text-slate-600' : 'bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-400'}`}
              onKeyDown={e => { if (e.ctrlKey && e.key === 'Enter') addComment(); }}
            />
            <div className="flex items-center justify-between mt-2">
              <label className="flex items-center gap-1.5 cursor-pointer">
                <input type="checkbox" checked={isInternal} onChange={e => setIsInternal(e.target.checked)} className="rounded" />
                <span className={`text-[11px] ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Internal note (not visible to client)</span>
              </label>
              <button
                onClick={addComment}
                disabled={!comment.trim() || saving}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${comment.trim() ? 'bg-blue-600 text-white hover:bg-blue-700' : isDark ? 'bg-[#222] text-slate-600' : 'bg-gray-100 text-gray-400'}`}
              >
                <Send size={12} /> Reply
              </button>
            </div>
          </div>
        </div>

        {/* Right: Properties */}
        <div className={`w-52 shrink-0 border-l p-4 space-y-4 overflow-y-auto ${isDark ? 'border-[#222]' : 'border-gray-100'}`}>
          <div>
            <label className={labelCls}>Status</label>
            <select value={editStatus} onChange={e => setEditStatus(e.target.value)} className={inputCls}>
              {STATUSES.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Priority</label>
            <select value={editPriority} onChange={e => setEditPriority(e.target.value)} className={inputCls}>
              {PRIORITIES.map(p => <option key={p}>{p}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>Assigned To</label>
            <input type="text" value={editAssignedTo} onChange={e => setEditAssignedTo(e.target.value)} placeholder="assignee username" className={inputCls} />
          </div>
          <button
            onClick={saveChanges}
            disabled={saving}
            className={`w-full py-2 rounded-lg text-xs font-semibold transition-colors ${isDark ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-blue-600 text-white hover:bg-blue-700'} disabled:opacity-50`}
          >
            {saving ? 'Saving…' : 'Save Changes'}
          </button>

          <div className={`pt-3 border-t space-y-2 ${isDark ? 'border-[#2a2a2a]' : 'border-gray-100'}`}>
            <div>
              <div className={labelCls}>Raised By</div>
              <div className={`text-xs font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{detail?.userId || ticket.userId}</div>
            </div>
            {(detail?.clientId || ticket.clientId) && (
              <div>
                <div className={labelCls}>Client</div>
                <div className={`text-xs font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>{detail?.clientId || ticket.clientId}</div>
              </div>
            )}
            <div>
              <div className={labelCls}>Category</div>
              <div className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{detail?.category}</div>
            </div>
            <div>
              <div className={labelCls}>Created</div>
              <div className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{fmtDate(detail?.createdAt)}</div>
            </div>
            <div>
              <div className={labelCls}>Updated</div>
              <div className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{timeAgo(detail?.updatedAt)}</div>
            </div>
            {detail?.closedAt && (
              <div>
                <div className={labelCls}>Closed</div>
                <div className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{fmtDate(detail.closedAt)}</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────
// Main Screen
// ─────────────────────────────────────────
export default function TicketManagementScreen({ theme = 'light' }) {
  const isDark = theme === 'dark';
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ status: 'all', priority: 'all', category: 'all' });
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [initDone, setInitDone] = useState(false);
  const LIMIT = 30;

  const cellCls = `px-3 py-2.5 text-xs ${isDark ? 'text-slate-300' : 'text-gray-700'}`;
  const thCls = `px-3 py-2 text-[10px] font-bold uppercase tracking-wider text-left ${isDark ? 'text-slate-500' : 'text-gray-400'}`;

  // ── refreshTickets: called from event handlers (Refresh button, onUpdated) ──
  const refreshTickets = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page), limit: String(LIMIT),
        status: filters.status, priority: filters.priority, category: filters.category
      });
      if (search) params.set('search', search);
      const [tRes, sRes] = await Promise.all([
        fetch(`${API}/tickets/all?${params}`, { headers: authHeaders() }),
        fetch(`${API}/stats/dashboard`, { headers: authHeaders() })
      ]);
      const [tData, sData] = await Promise.all([tRes.json(), sRes.json()]);
      if (tData.success) { setTickets(tData.data); setTotal(tData.total); }
      if (sData.success) setStats(sData.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  // ── useEffect — inline async IIFE with all deps in closure ───────────────────
  useEffect(() => {
    const load = async () => {
      if (!initDone) {
        try { await fetch(`${API}/init-tables`, { method: 'POST', headers: authHeaders() }); } catch (_init) { /* best-effort */ }
        setInitDone(true);
      }
      setLoading(true);
      try {
        const params = new URLSearchParams({
          page: String(page), limit: String(LIMIT),
          status: filters.status, priority: filters.priority, category: filters.category
        });
        if (search) params.set('search', search);
        const [tRes, sRes] = await Promise.all([
          fetch(`${API}/tickets/all?${params}`, { headers: authHeaders() }),
          fetch(`${API}/stats/dashboard`, { headers: authHeaders() })
        ]);
        const [tData, sData] = await Promise.all([tRes.json(), sRes.json()]);
        if (tData.success) { setTickets(tData.data); setTotal(tData.total); }
        if (sData.success) setStats(sData.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [page, filters, search, initDone]);

  return (
    <div data-testid="ticket-management-screen" className={`flex flex-col h-full ${isDark ? 'bg-[#0e0e10] text-white' : 'bg-[#f8f9fb] text-gray-900'}`}>
      {/* Header */}
      <div className={`shrink-0 flex items-center gap-3 px-4 py-3 border-b ${isDark ? 'bg-[#111] border-[#1f1f1f]' : 'bg-white border-gray-200'}`}>
        <Ticket size={18} className="text-purple-500 shrink-0" />
        <div>
          <h1 className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Ticket Management</h1>
          <p className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Support team — all client tickets · Two-way ERP integration</p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className={`text-xs ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{total} total</span>
          <button onClick={refreshTickets} className={`p-1.5 rounded-lg ${isDark ? 'hover:bg-white/10 text-slate-400' : 'hover:bg-gray-100 text-gray-500'}`}>
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* Stats */}
      <StatsBar stats={stats} theme={theme} />

      {/* Filters + Search */}
      <div className={`shrink-0 flex items-center gap-2 px-4 py-2 border-b ${isDark ? 'bg-[#111] border-[#1f1f1f]' : 'bg-white border-gray-100'}`}>
        <div className={`flex items-center gap-2 flex-1 px-3 py-1.5 rounded-lg border ${isDark ? 'bg-[#1a1a1a] border-[#333]' : 'bg-gray-50 border-gray-200'}`}>
          <Search size={13} className={isDark ? 'text-slate-500' : 'text-gray-400'} />
          <input
            type="text"
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search by ticket#, subject, user…"
            className={`flex-1 bg-transparent text-xs outline-none ${isDark ? 'text-white placeholder:text-slate-600' : 'text-gray-900 placeholder:text-gray-400'}`}
            data-testid="ticket-search"
          />
          {search && <button onClick={() => setSearch('')}><X size={12} className={isDark ? 'text-slate-500' : 'text-gray-400'} /></button>}
        </div>

        {[['status', STATUSES], ['priority', PRIORITIES], ['category', CATEGORIES]].map(([key, opts]) => (
          <select
            key={key}
            value={filters[key]}
            onChange={e => { setFilters(f => ({ ...f, [key]: e.target.value })); setPage(1); }}
            className={`px-2 py-1.5 rounded-lg border text-xs ${isDark ? 'bg-[#1a1a1a] border-[#333] text-slate-300' : 'bg-gray-50 border-gray-200 text-gray-700'}`}
            data-testid={`filter-${key}`}
          >
            <option value="all">{key.charAt(0).toUpperCase() + key.slice(1)}: All</option>
            {opts.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ))}
      </div>

      {/* Main Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Ticket List */}
        <div className={`flex flex-col ${selectedTicket ? 'w-[45%] shrink-0' : 'flex-1'} overflow-hidden`}>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex justify-center py-12"><RefreshCw size={22} className="animate-spin text-blue-500" /></div>
            ) : tickets.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Ticket size={40} className={isDark ? 'text-slate-700' : 'text-gray-300'} />
                <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>No tickets found</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className={`sticky top-0 z-10 ${isDark ? 'bg-[#0f0f11]' : 'bg-gray-50'} border-b ${isDark ? 'border-[#1f1f1f]' : 'border-gray-100'}`}>
                  <tr>
                    <th className={thCls}>Ticket</th>
                    <th className={thCls}>Subject</th>
                    <th className={thCls}>Client/User</th>
                    <th className={thCls}>Status</th>
                    <th className={thCls}>Priority</th>
                    <th className={thCls}>Assigned</th>
                    <th className={thCls}>Updated</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map(t => (
                    <tr
                      key={t.id}
                      onClick={() => setSelectedTicket(t)}
                      data-testid={`ticket-row-${t.id}`}
                      className={`border-b cursor-pointer transition-colors ${
                        selectedTicket?.id === t.id
                          ? isDark ? 'bg-blue-600/10' : 'bg-blue-50'
                          : isDark ? 'hover:bg-white/[0.02] border-[#1a1a1a]' : 'hover:bg-gray-50 border-gray-50'
                      }`}
                    >
                      <td className={`${cellCls} font-mono`}>
                        <span className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>{t.ticketNumber}</span>
                        {t.commentCount > 0 && (
                          <span className={`ml-1.5 text-[9px] ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>
                            <MessageSquare size={9} className="inline" /> {t.commentCount}
                          </span>
                        )}
                      </td>
                      <td className={cellCls}>
                        <span className={`font-medium line-clamp-1 text-[11px] ${isDark ? 'text-white' : 'text-gray-900'}`}>{t.subject}</span>
                        {t.category && <span className={`text-[9px] ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>{t.category}</span>}
                      </td>
                      <td className={cellCls}>
                        <div className={`text-[11px] ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{t.userId}</div>
                        {t.clientId && <div className={`text-[10px] ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>{t.clientId}</div>}
                      </td>
                      <td className={cellCls}>
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusColor(t.status)}`}>
                          {statusIcon(t.status)}{t.status}
                        </span>
                      </td>
                      <td className={cellCls}>
                        <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${priorityColor(t.priority)}`}>{t.priority}</span>
                      </td>
                      <td className={`${cellCls} text-[11px]`}>
                        {t.assignedTo
                          ? <span className={`flex items-center gap-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}><User size={10} />{t.assignedTo}</span>
                          : <span className={isDark ? 'text-slate-600 italic' : 'text-gray-400 italic'}>Unassigned</span>}
                      </td>
                      <td className={`${cellCls} text-[10px] ${isDark ? 'text-slate-600' : 'text-gray-400'}`}>{timeAgo(t.updatedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Pagination */}
          {total > LIMIT && (
            <div className={`shrink-0 flex items-center justify-between px-4 py-2 border-t ${isDark ? 'border-[#1f1f1f]' : 'border-gray-100'}`}>
              <span className={`text-[10px] ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)} of {total}
              </span>
              <div className="flex gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className={`px-2 py-1 rounded text-[10px] ${isDark ? 'bg-[#222] text-slate-400 disabled:opacity-30' : 'bg-gray-100 text-gray-600 disabled:opacity-30'}`}>Prev</button>
                <button onClick={() => setPage(p => p + 1)} disabled={page * LIMIT >= total}
                  className={`px-2 py-1 rounded text-[10px] ${isDark ? 'bg-[#222] text-slate-400 disabled:opacity-30' : 'bg-gray-100 text-gray-600 disabled:opacity-30'}`}>Next</button>
              </div>
            </div>
          )}
        </div>

        {/* Detail Panel */}
        {selectedTicket && (
          <TicketDetail
            key={selectedTicket.id}
            ticket={selectedTicket}
            onClose={() => setSelectedTicket(null)}
            onUpdated={refreshTickets}
            theme={theme}
          />
        )}
      </div>
    </div>
  );
}
